import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { onAuthStateChanged, signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import type { FirebaseError } from "firebase/app";
import { Loader2 } from "lucide-react";
import { auth } from "@/lib/firebase";
import {
  approveMcpAuthorize,
  denyMcpAuthorize,
  getMcpAuthorizeRequest,
} from "@/features/auth/lib/mcp-authorize";
import {
  ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL,
  POPUP_PROVIDER_COPY,
  POPUP_PROVIDERS,
  isPopupDismissed,
  type PopupProviderId,
} from "@/features/auth/lib/popup-sign-in";
import { ProviderSignInButton } from "@/features/auth/components/ProviderSignInButton";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Sentry } from "@/lib/sentry";
import {
  AdgeLogo,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PasswordInput,
} from "@nexus/ui";

export const Route = createFileRoute("/auth/mcp-authorize")({
  validateSearch: (search: Record<string, unknown>) => ({
    request_id: search.request_id != null ? String(search.request_id) : "",
  }),
  component: McpAuthorizePage,
});

type Stage = "loading" | "signin" | "consent" | "redirecting" | "done" | "error";

/** How long to wait for the client redirect to unload this page before showing done. */
const REDIRECT_HANDOFF_FALLBACK_MS = 1_500;

/**
 * Consent screen for the MCP browser login leg.
 *
 * Sits outside `_authenticated` so the Nexus session guard never redirects
 * away and loses the request id. Approving here does NOT call
 * `/auth/exchange`: an MCP grant must not create or refresh a web session,
 * which is the whole point of the separate signing key.
 */
function McpAuthorizePage() {
  const { t } = useTranslation();
  useDocumentTitle(t("page.title.mcpAuthorize"));
  const { request_id: requestId } = useSearch({ from: "/auth/mcp-authorize" });

  const [stage, setStage] = useState<Stage>("loading");
  const [clientName, setClientName] = useState<string>("");
  const [signedInEmail, setSignedInEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!requestId) {
      setError(t("auth.mcpRequestMissing"));
      setStage("error");
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | undefined;
    void (async () => {
      try {
        const info = await getMcpAuthorizeRequest(requestId);
        if (cancelled) return;
        setClientName(info.clientName);
        // Firebase restores the session asynchronously, so read it through the
        // observer rather than `auth.currentUser`, which is null on first paint
        // even for a signed-in user.
        unsubscribe = onAuthStateChanged(auth, (user) => {
          if (cancelled) return;
          setSignedInEmail(user?.email ?? null);
          // Do not clobber redirecting/done/error after the OAuth handoff starts.
          setStage((current) => {
            if (current === "redirecting" || current === "done" || current === "error") {
              return current;
            }
            return user ? "consent" : "signin";
          });
        });
        // Cleanup may have run while we awaited the request metadata.
        if (cancelled) unsubscribe();
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t("auth.mcpRequestExpired"));
        setStage("error");
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [requestId, t]);

  useEffect(() => {
    if (stage !== "redirecting") return;
    const timer = window.setTimeout(() => {
      setStage("done");
      window.close();
    }, REDIRECT_HANDOFF_FALLBACK_MS);
    return () => window.clearTimeout(timer);
  }, [stage]);

  const handleSignedIn = useCallback((email: string | null) => {
    setSignedInEmail(email);
    setStage("consent");
  }, []);

  /**
   * Deliver the OAuth code (or error) to the chat client. A separate effect
   * shows a done screen and tries to close the window when custom-scheme /
   * loopback redirects leave this document loaded.
   */
  function handoffToClient(redirectUri: string) {
    setStage("redirecting");
    window.location.replace(redirectUri);
  }

  async function handleApprove() {
    setSubmitting(true);
    setError(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error(t("auth.mcpNotSignedIn"));
      const firebaseToken = await user.getIdToken(true);
      const { redirectUri } = await approveMcpAuthorize(requestId, firebaseToken);
      handoffToClient(redirectUri);
    } catch (err) {
      Sentry.captureException(err, { tags: { flow: "mcp-authorize" } });
      setError(err instanceof Error ? err.message : t("auth.mcpApproveError"));
      setSubmitting(false);
    }
  }

  async function handleDeny() {
    setSubmitting(true);
    try {
      const { redirectUri } = await denyMcpAuthorize(requestId);
      handoffToClient(redirectUri);
    } catch (err) {
      Sentry.captureException(err, { tags: { flow: "mcp-authorize-deny" } });
      setError(err instanceof Error ? err.message : t("auth.mcpApproveError"));
      setSubmitting(false);
    }
  }

  if (stage === "loading" || stage === "redirecting") {
    return (
      <McpAuthorizeShell title={t("auth.mcpTitle")} description={t("auth.mcpLoading")}>
        <div className="flex justify-center py-2">
          <Loader2 className="h-5 w-5 animate-spin text-[var(--muted-foreground)]" aria-hidden="true" />
          <span className="sr-only">{t("common.loading")}</span>
        </div>
      </McpAuthorizeShell>
    );
  }

  if (stage === "done") {
    return (
      <McpAuthorizeShell title={t("auth.mcpDoneTitle")} description={t("auth.mcpDoneDescription")}>
        <p className="text-center text-xs text-[var(--muted-foreground)]">
          {t("auth.mcpDoneHelp")}
        </p>
      </McpAuthorizeShell>
    );
  }

  if (stage === "error") {
    return (
      <McpAuthorizeShell title={t("auth.mcpFailedTitle")} description={error ?? ""}>
        <p className="text-center text-xs text-[var(--muted-foreground)]">
          {t("auth.mcpFailedHelp")}
        </p>
      </McpAuthorizeShell>
    );
  }

  if (stage === "signin") {
    return (
      <McpAuthorizeShell
        title={t("auth.mcpTitle")}
        description={t("auth.mcpSignInPrompt", { client: clientName })}
      >
        <McpSignInForm onSignedIn={handleSignedIn} />
      </McpAuthorizeShell>
    );
  }

  return (
    <McpAuthorizeShell
      title={t("auth.mcpConsentTitle", { client: clientName })}
      description={t("auth.mcpConsentDescription", { client: clientName })}
    >
      <ul className="space-y-2 rounded-md bg-[var(--muted)] p-3 text-sm text-[var(--foreground)]">
        <li>{t("auth.mcpScopeRead")}</li>
        <li>{t("auth.mcpScopePermissions")}</li>
      </ul>
      {signedInEmail && (
        <p className="text-center text-xs text-[var(--muted-foreground)] break-all">
          {t("auth.mcpSignedInAs", { email: signedInEmail })}
        </p>
      )}
      {error && (
        <p role="alert" className="text-center text-xs text-[var(--destructive)]">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={() => void handleDeny()}
          disabled={submitting}
        >
          {t("auth.mcpDeny")}
        </Button>
        <Button
          type="button"
          className="flex-1 gap-2"
          onClick={() => void handleApprove()}
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          <span>{t("auth.mcpApprove")}</span>
        </Button>
      </div>
    </McpAuthorizeShell>
  );
}

function McpAuthorizeShell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-svh items-center justify-center bg-[var(--background)] p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1 text-start">
          <AdgeLogo className="mb-2 origin-start" aria-label={t("auth.brandName")} />
          <CardTitle className="text-xl font-normal">{title}</CardTitle>
          <CardDescription className="text-sm">{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">{children}</CardContent>
      </Card>
    </div>
  );
}

/**
 * Firebase-only sign-in. Unlike `LoginForm` this never exchanges the ID token
 * for a Nexus JWT and never navigates — the caller stays on this page with the
 * request id intact.
 */
function McpSignInForm({ onSignedIn }: { onSignedIn: (email: string | null) => void }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [popupLoading, setPopupLoading] = useState<PopupProviderId | null>(null);
  const anyLoading = loading || popupLoading !== null;

  async function handlePasswordLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email || !password) return;
    setFieldError(null);
    setLoading(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      onSignedIn(credential.user.email);
    } catch {
      setFieldError(t("auth.loginError"));
    } finally {
      setLoading(false);
    }
  }

  async function handlePopupLogin(provider: PopupProviderId) {
    setPopupLoading(provider);
    try {
      const credential = await signInWithPopup(auth, POPUP_PROVIDERS[provider]);
      onSignedIn(credential.user.email);
    } catch (err) {
      const code = (err as FirebaseError).code;
      if (isPopupDismissed(code)) return;
      if (code === ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL) {
        setFieldError(t("auth.accountExistsWithDifferentProvider"));
        return;
      }
      Sentry.captureException(err, { tags: { flow: `mcp-${provider}-signin`, errorCode: code } });
      setFieldError(t(POPUP_PROVIDER_COPY[provider].error));
    } finally {
      setPopupLoading(null);
    }
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handlePasswordLogin} className="space-y-4" noValidate>
        <div className="space-y-2">
          <Label htmlFor="mcp-email">{t("auth.email")}</Label>
          <Input
            id="mcp-email"
            type="email"
            placeholder={t("auth.emailPlaceholder")}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (fieldError) setFieldError(null);
            }}
            required
            autoComplete="email"
            disabled={anyLoading}
            aria-invalid={fieldError ? true : undefined}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="mcp-password">{t("auth.password")}</Label>
          <PasswordInput
            id="mcp-password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (fieldError) setFieldError(null);
            }}
            required
            autoComplete="current-password"
            disabled={anyLoading}
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={fieldError ? "mcp-login-error" : undefined}
            showPasswordLabel={t("auth.showPassword")}
            hidePasswordLabel={t("auth.hidePassword")}
          />
          {fieldError && (
            <p id="mcp-login-error" role="alert" className="text-xs text-[var(--destructive)]">
              {fieldError}
            </p>
          )}
        </div>
        <Button type="submit" className="w-full gap-2" disabled={anyLoading} aria-busy={loading}>
          {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          <span>{loading ? t("auth.signingIn") : t("auth.signIn")}</span>
        </Button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-[var(--card)] px-2 text-[var(--muted-foreground)]">
            {t("auth.orDivider")}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <ProviderSignInButton
          provider="google"
          isLoading={popupLoading === "google"}
          disabled={anyLoading}
          onClick={() => void handlePopupLogin("google")}
        />
        <ProviderSignInButton
          provider="microsoft"
          isLoading={popupLoading === "microsoft"}
          disabled={anyLoading}
          onClick={() => void handlePopupLogin("microsoft")}
        />
      </div>
    </div>
  );
}
